import { useCallback, useMemo, useRef } from 'react';

import { type IntentConfiguration } from '@stripe/stripe-react-native';
import { useActorRef, useSelector } from '@xstate5/react';
import * as DateFns from 'date-fns';
import { ActorRef } from 'xstate5';

import {
  PaymentStatePollingActors,
  usePaymentStatePollingMachine,
} from '@sg/shared-fe-features-ordering';

import { useAwsWafTokenRef } from '@order/integrations-aws-waf';
import { useFeatureFlag } from '@order/LaunchDarkly';

import {
  useBagLoyaltyRewardsDeferred,
  useFetchPaymentBreakdown,
  useOrderPaymentStateErrorHandler,
  useShowOrderPaymentStateErrorOnVisible,
} from '../hooks';
import {
  adjustWantedTime,
  prepareLedger,
  clearLocalStorageOrderId,
  getOrderPaymentStateConfig,
} from '../utils';
import { orderingMachine } from './ordering-machine';
import {
  type ConvertToGroupOrderErrorParams,
  type LeaveGroupOrderErrorParams,
  type LockGroupOrderErrorParams,
  type OrderingMachineAction,
} from './ordering-machine.actions.types';
import { type ConvertToGroupOrderParams, type OrderingActors } from './ordering-machine.actors';
import { type WantedTime } from './ordering-machine.types';
import { getMaxAvailableUpsellItems } from './ordering-machine.utils';

import type { BagReward, OrderingContext } from './ordering-machine.context';
import type {
  AddUpsellToBag,
  ChangeDeliveryPreferences,
  ChangeLineItemQuantity,
  ChangeUtensilsUsage,
  OrderingEvent,
} from './ordering-machine.events';

// ─────────────────────────────────────────────────────────────────────────────

export const useOrderingMachine = (params: UseOrderingParams) => {
  const {
    isLoggedIn,
    orderSubmissionDelay,
    bagCartPollingInterval,
    bagTracking,
    handlePostOrderPurchaseModal,
    handlePostOrderNotifications,
    handlePostOrderTracking,
    clearSharedLineItemAttribution,
    handlePostOrderHistory,
    handlePostOrderAppReview,
    navigatePostOrder,
    changeChannelOrLocation,
    requestLogin,
    fetchInitialData,
    fetchOrderPaymentState,
    fetchStatePostOrder,
    fetchRewards,
    fetchWantedTimes,
    fetchBagCartPolling,
    addUpsellToBag,
    updateLineItem,
    applyReward,
    removeReward,
    applyPromoCode,
    applyGiftCard,
    cancelPreviousIntent,
    submitOrder,
    convertToGroupOrder: convertToGroupOrderActor,
    handleConvertToGroupOrderError,
    leaveGroupOrder: leaveGroupOrderActor,
    handleLeaveGroupOrderSuccess,
    handleLeaveGroupOrderError,
    lockGroupOrder: lockGroupOrderActor,
    handleLockGroupOrderError,
    unlockGroupOrder: unlockGroupOrderActor,
    unlockCartInBackground,
    closeBag: closeBagAction,
    handleUnlockGroupOrderError,
  } = params;

  // ─── Tracking ────────────────────────────────────────────────────────────────

  const {
    trackBagView,
    trackLocationNamePressed,
    trackWantedTimePressed,
    trackWantedTimeChanged,
    trackDeliveryPreferencesPressed,
    trackDropoffLocationChanged,
    trackLineItemQuantityChanged,
    trackLineItemRemoved,
    trackUtensilsUsagePressed,
    trackLoyaltyInfoPressed,
    trackLoyaltyTermsPressed,
    trackApplyRewardPressed,
    trackRemoveRewardPressed,
    trackPromoCodeAddPressed,
    trackChangePaymentMethodPressed,
    trackCreditPressed,
    trackChangeFlexUsagePressed,
    trackRedeemGiftCardPressed,
    trackRedeemGiftCardSubmitted,
    trackOrderPlaybackAutoSubmit,
    trackOrderPlaybackGoBack,
  } = bagTracking;

  // ─── Stripe ────────────────────────────────────────────────────────────────

  const stripeConfirmHandlerRef = useRef<NonNullable<IntentConfiguration['confirmHandler']>>(
    () => {},
  );

  const registerStripeConfirmHandler = useCallback(
    (handler: NonNullable<IntentConfiguration['confirmHandler']>) => {
      stripeConfirmHandlerRef.current = handler;
    },
    [],
  );

  // ─── Payment State Polling ─────────────────────────────────────────────────

  const orderPaymentStateConfig = useFeatureFlag('CELS-4828-order-payment-state-config');
  const maxNumberOfUpsellProducts = useFeatureFlag('permanent-order-upsell-number-of-products');

  const handleTimeslotUnavailable = useCallback(() => {
    actorRefRef.current?.send({ type: 'TIMESLOT_UNAVAILABLE' });
  }, []);

  const { handleOnVisibleRef, onOrderPaymentStateError } = useOrderPaymentStateErrorHandler({
    handleTimeslotUnavailable,
  });

  const {
    paymentStatePollingInterval,
    paymentStateExpirationTolerance,
    paymentStateDelayedTolerance,
  } = getOrderPaymentStateConfig(orderPaymentStateConfig);

  const actorRefRef = useRef<ActorRef<any, OrderingEvent>>(null);

  const incrementPurchaseAttempt = useCallback(() => {
    actorRefRef.current?.send({ type: 'INCREMENT_PURCHASE_ATTEMPT' });
  }, []);

  const onPaymentFulfilled = useCallback(() => {
    actorRefRef.current?.send({ type: 'PAYMENT_FULFILLED' });
  }, []);

  const onPaymentCanceled = useCallback(() => {
    actorRefRef.current?.send({ type: 'PAYMENT_CANCELED' });
  }, []);

  const handlePaymentError = useCallback(
    (params: Parameters<typeof onOrderPaymentStateError>[0]) => {
      actorRefRef.current?.send({ type: 'PAYMENT_CANCELED' });
      onOrderPaymentStateError(params);
    },
    [onOrderPaymentStateError],
  );

  const {
    paymentState,
    startPolling,
    createPayment,
    clearPaymentState,
    failPayment,
    requestPaymentCancelation,
    isPollingPayment,
    isPaymentPending,
    isPaymentDelayed,
    isPaymentFulfilled,
    isCancelingPayment,
  } = usePaymentStatePollingMachine({
    paymentStatePollingInterval,
    paymentStateExpirationTolerance,
    paymentStateDelayedTolerance,
    fetchPaymentState: fetchOrderPaymentState,
    cancelPayment: cancelPreviousIntent,
    clearPendingPayment: clearLocalStorageOrderId,
    incrementPurchaseAttempt,
    onPaymentStateError: handlePaymentError,
    onPaymentFulfilled,
    onPaymentCanceled,
  });

  const startPollingPaymentState = useCallback(
    (params: { context: OrderingContext }) => {
      const { context } = params;
      startPolling(context.orderId ?? context.cart?.orderId ?? '');
    },
    [startPolling],
  );

  // ─── Guards ────────────────────────────────────────────────────────────────

  const checkIfLoggedIn = useCallback(() => {
    return isLoggedIn;
  }, [isLoggedIn]);

  const checkIfShouldFetchInitialPaymentState = useCallback(() => {
    return isLoggedIn;
  }, [isLoggedIn]);

  const checkIfBagCartPollingEnabled = useCallback(() => {
    return bagCartPollingInterval > 0;
  }, [bagCartPollingInterval]);

  // ─── Actions ─────────────────────────────────────────────────────────────────

  const wantedTimesPollingInterval = useFeatureFlag('permanent-bag-wanted-times-polling-interval-milliseconds'); // prettier-ignore
  const { fetchPaymentBreakdown } = useFetchPaymentBreakdown();

  const actorRef = useActorRef(
    orderingMachine.provide({
      actions: {
        handlePostOrderPurchaseModal,
        handlePostOrderNotifications,
        handlePostOrderTracking,
        clearSharedLineItemAttribution,
        handlePostOrderHistory,
        handlePostOrderAppReview,
        navigatePostOrder,
        changeChannelOrLocation,
        requestLogin,
        handleConvertToGroupOrderError,
        handleLeaveGroupOrderSuccess,
        handleLeaveGroupOrderError,
        handleLockGroupOrderError,
        unlockCartInBackground,
        closeBag: closeBagAction,
        handleUnlockGroupOrderError,
        clearLocalStorageOrderId,
        startPollingPaymentState,
        trackOrderPlaybackAutoSubmit,
        trackOrderPlaybackGoBack,
      },
      actors: {
        fetchInitialData,
        fetchStatePostOrder,
        fetchRewards,
        fetchWantedTimes,
        fetchBagCartPolling,
        fetchPaymentBreakdown,
        addUpsellToBag,
        updateLineItem,
        applyReward,
        removeReward,
        applyPromoCode,
        applyGiftCard,
        submitOrder,
        convertToGroupOrder: convertToGroupOrderActor,
        leaveGroupOrder: leaveGroupOrderActor,
        lockGroupOrder: lockGroupOrderActor,
        unlockGroupOrder: unlockGroupOrderActor,
      },
      guards: {
        checkIfLoggedIn,
        checkIfShouldFetchInitialPaymentState,
        checkIfBagCartPollingEnabled,
      },
    }),
    {
      input: {
        orderSubmissionDelay,
        wantedTimesPollingInterval,
        bagCartPollingInterval,
        stripeConfirmHandlerRef,
        maxNumberOfUpsellProducts,
      },
    },
  );

  actorRefRef.current = actorRef;
  const context = useSelector(actorRef, (snapshot) => snapshot.context);
  useShowOrderPaymentStateErrorOnVisible(paymentState, handleOnVisibleRef);

  // ─── Flags ───────────────────────────────────────────────────────────

  const isFetchingInitialData = useSelector(actorRef, (state) => {
    return (
      state.matches({ ordering: { loading: 'fetchingInitialData' } }) ||
      state.matches({ ordering: { loading: 'retrievingInitialWantedTime' } })
    );
  });

  const isFetchingRewards = useSelector(actorRef, (state) => {
    return (
      state.matches({ rewards: 'preparingToFetchRewards' }) ||
      state.matches({ rewards: 'fetchingRewards' }) ||
      state.matches({ rewards: 'fetchingRewardsDeferred' })
    );
  });

  const isFetchingRewardsDeferred = useSelector(actorRef, (state) => {
    return state.matches({ rewards: 'fetchingRewardsDeferred' });
  });

  const hasFailedToLoadRewards = useSelector(actorRef, (state) => {
    return state.matches({ rewards: 'error' });
  });

  const isChangingLineItemQuantity = useSelector(actorRef, (state) => {
    return state.matches({
      ordering: { loading: 'changingLineItemQuantity' },
    });
  });

  const isApplyingPromoCode = useSelector(actorRef, (state) => {
    return state.matches({
      ordering: { loading: 'applyingPromoCode' },
    });
  });

  const isChangingTime = useSelector(actorRef, (state) => {
    return state.matches({ ordering: { idle: 'changingTime' } });
  });

  const isChangingDeliveryPreferences = useSelector(actorRef, (state) => {
    return state.matches({
      ordering: { idle: 'changingDeliveryPreferences' },
    });
  });

  const isSubmittingOrder = useSelector(actorRef, (state) => {
    return (
      isPaymentPending ||
      state.matches({ ordering: { loading: 'confirmingStripe' } }) ||
      state.matches({ ordering: { loading: 'submittingOrder' } }) ||
      state.matches({ ordering: { idle: 'postOrder' } }) ||
      state.matches({ ordering: { loading: 'fetchingStatePostOrder' } })
    );
  });

  const isPostOrder = useSelector(actorRef, (state) => {
    return state.matches({ ordering: { idle: 'postOrder' } });
  });

  const isApplyingGiftCard = useSelector(actorRef, (state) => {
    return state.matches({
      ordering: { loading: 'applyingGiftCard' },
    });
  });

  const isAddingUpsellToBag = useSelector(actorRef, (state) => {
    return state.matches({
      ordering: { loading: 'addingUpsellToBag' },
    });
  });

  const isLineItemQuantityChangePending = useSelector(actorRef, (state) => {
    return state.context.lineItemIdsBeingUpdated.length > 0;
  });

  const isShowingGroupOrderLeaveModal = useSelector(actorRef, (state) => {
    return (
      state.matches({ 'group-ordering': 'confirming-leave' }) ||
      state.matches({ 'group-ordering': 'leaving' })
    );
  });

  const isShowingGroupOrderConvertModal = useSelector(actorRef, (state) => {
    return (
      state.matches({ 'group-ordering': 'confirming-conversion' }) ||
      state.matches({ 'group-ordering': 'converting' })
    );
  });

  const isConvertingGroupOrder = useSelector(actorRef, (state) => {
    return state.matches({ 'group-ordering': 'converting' });
  });

  const isShowingGroupOrderShareModal = useSelector(actorRef, (state) => {
    return state.matches({ 'group-ordering': 'sharing' });
  });

  const isLeavingGroupOrder = useSelector(actorRef, (state) => {
    return state.matches({ 'group-ordering': 'leaving' });
  });

  const isShowingGroupOrderLockModal = useSelector(actorRef, (state) => {
    return (
      state.matches({ 'group-ordering': 'confirming-lock' }) ||
      state.matches({ 'group-ordering': 'locking' })
    );
  });

  const isLockingGroupOrder = useSelector(actorRef, (state) => {
    return state.matches({ 'group-ordering': 'locking' });
  });

  const isUnlockingGroupOrder = useSelector(actorRef, (state) => {
    return state.matches({ 'group-ordering': 'unlocking' });
  });

  const isPerformingBagAction = useSelector(actorRef, (state) => {
    const { context: ctx } = state;
    const isRefetchingRewards =
      ctx.rewards.length > 0 &&
      (state.matches({ rewards: 'fetchingRewards' }) ||
        state.matches({ rewards: 'fetchingRewardsDeferred' }));

    return (
      Boolean(ctx.rewardIdBeingUpdated) ||
      isRefetchingRewards ||
      state.matches({ ordering: 'loading' }) ||
      state.matches({ 'group-ordering': 'converting' }) ||
      state.matches({ 'group-ordering': 'leaving' }) ||
      state.matches({ 'group-ordering': 'locking' }) ||
      state.matches({ 'group-ordering': 'unlocking' })
    );
  });

  const isShowingFutureDateWarning = useSelector(actorRef, (state) => {
    return state.matches({
      ordering: { idle: 'showingFutureDateWarning' },
    });
  });

  const isShowingRewardWarning = useSelector(actorRef, (state) => {
    return state.matches({
      ordering: { idle: 'showingRewardWarning' },
    });
  });

  const isShowingWarning = useSelector(actorRef, (state) => {
    return (
      state.matches({
        ordering: { idle: 'showingRewardWarning' },
      }) ||
      state.matches({
        ordering: { idle: 'showingFutureDateWarning' },
      }) ||
      state.matches({
        ordering: { idle: 'timeslotAdjustedNotice' },
      })
    );
  });

  const isShowingCheckoutLedger = useSelector(actorRef, (state) => {
    return (
      state.matches({ ordering: { idle: 'checkoutLedger' } }) ||
      state.matches({ ordering: { loading: 'fetchingPaymentBreakdown' } })
    );
  });

  const isFetchingPaymentBreakdown = useSelector(actorRef, (state) => {
    return state.matches({ ordering: { loading: 'fetchingPaymentBreakdown' } });
  });

  const areUpsellInteractionsDisabled = isPerformingBagAction || isLineItemQuantityChangePending;

  const isShowingOrderPlayback = useSelector(actorRef, (state) => {
    const { context } = state;

    const isNotPostRedirect = context.hasTriggeredOrderSubmission;
    const isOrderPlayback = state.matches({ ordering: { idle: 'orderPlayback' } });

    // Our loading state for order submission is the order playback view.
    const shouldShowOrderPlayback = isOrderPlayback || isSubmittingOrder;

    // After redirect we don't want to show order playback again.
    return isNotPostRedirect && shouldShowOrderPlayback;
  });

  const isShowingTimeslotAdjustedNotice = useSelector(actorRef, (state) => {
    return state.matches({
      ordering: { idle: 'timeslotAdjustedNotice' },
    });
  });

  const isShowingNextDayOrderNotice = useSelector(actorRef, (state) => {
    return state.matches({
      ordering: { idle: 'nextDayOrderNotice' },
    });
  });

  const isChangingPaymentMethod = useSelector(actorRef, (state) => {
    return state.matches({
      ordering: { idle: 'changingPaymentMethod' },
    });
  });

  const isShowingGiftCardForm = useSelector(actorRef, (state) => {
    return (
      state.matches({
        ordering: { loading: 'applyingGiftCard' },
      }) || state.matches({ ordering: { idle: 'giftCardForm' } })
    );
  });

  const isShowingLoyaltyInfo = useSelector(actorRef, (state) => {
    return state.matches({ ordering: { idle: 'loyaltyInfo' } });
  });

  const isShowingLoyaltyTerms = useSelector(actorRef, (state) => {
    return state.matches({ ordering: { idle: 'loyaltyTerms' } });
  });

  const isShowingModal =
    isChangingTime ||
    isChangingDeliveryPreferences ||
    isShowingCheckoutLedger ||
    isChangingPaymentMethod ||
    isShowingGiftCardForm ||
    isShowingLoyaltyInfo ||
    isShowingLoyaltyTerms ||
    isShowingGroupOrderConvertModal ||
    isShowingGroupOrderShareModal ||
    isShowingGroupOrderLeaveModal ||
    isApplyingGiftCard ||
    isShowingNextDayOrderNotice ||
    isShowingOrderPlayback;

  const hasLineItems = (context?.cart?.lineItems?.length ?? 0) > 0;
  const isBagEmpty = !isFetchingInitialData && !hasLineItems && !isShowingModal;
  const isBagValid = !isFetchingInitialData && hasLineItems && !isPostOrder;

  // ─── Actions ─────────────────────────────────────────────────────────────────

  const startOrdering = useCallback(() => {
    actorRef.send({ type: 'START' });
    trackBagView();
  }, [actorRef, trackBagView]);

  const startPostRedirectDelay = useCallback(() => {
    actorRef.send({ type: 'START_POST_REDIRECT_DELAY' });
  }, [actorRef]);

  const cancelRequest = useCallback(
    (overrideCheck = false) => {
      const shouldOverride = typeof overrideCheck === 'boolean' ? overrideCheck : false; // prettier-ignore
      // Prevents cancelling requests (closing modals) during order submission.
      // This check will be overwritten if the submission fails and needs to be canceled.
      if (isSubmittingOrder && !shouldOverride) return;

      actorRef.send({ type: 'CANCEL_REQUEST' });
    },
    [actorRef, isSubmittingOrder],
  );

  const setStripeFailedToLoad = useCallback(() => {
    actorRef.send({ type: 'STRIPE_FAILED_TO_LOAD' });
  }, [actorRef]);

  const flagFailureAsShown = useCallback(() => {
    actorRef.send({ type: 'SET_STRIPE_FAILURE_AS_ALREADY_SHOWN' });
  }, [actorRef]);

  const setConfirmStripe = useCallback(
    (confirmStripe: () => Promise<void>) => {
      actorRef.send({ type: 'SET_CONFIRM_STRIPE', params: { confirmStripe } });
    },
    [actorRef],
  );

  const requestChannelOrLocationChange = useCallback(() => {
    actorRef.send({ type: 'REQUEST_CHANNEL_LOCATION_CHANGE' });
    trackLocationNamePressed();
  }, [actorRef, trackLocationNamePressed]);

  const requestTimeChange = useCallback(() => {
    actorRef.send({ type: 'REQUEST_TIME_CHANGE' });
    trackWantedTimePressed();
  }, [actorRef, trackWantedTimePressed]);

  const changeTime = useCallback(
    (wantedTime: WantedTime) => {
      const validTime = adjustWantedTime(wantedTime);
      const isFutureDate = validTime ? DateFns.isTomorrow(validTime) : false;
      actorRef.send({
        type: 'CHANGE_TIME',
        params: { wantedTime, isFutureDate },
      });
      trackWantedTimeChanged();
    },
    [actorRef, trackWantedTimeChanged],
  );

  const startWantedTimesPolling = useCallback(() => {
    actorRef.send({ type: 'START_WANTED_TIMES_POLLING' });
  }, [actorRef]);

  const stopWantedTimesPolling = useCallback(() => {
    actorRef.send({ type: 'STOP_WANTED_TIMES_POLLING' });
  }, [actorRef]);

  const startBagCartPolling = useCallback(() => {
    actorRef.send({ type: 'START_CART_POLLING' });
  }, [actorRef]);

  const stopBagCartPolling = useCallback(() => {
    actorRef.send({ type: 'STOP_CART_POLLING' });
  }, [actorRef]);

  const changeTip = useCallback(
    (tip: number) => {
      actorRef.send({ type: 'CHANGE_TIP', params: { tip } });
    },
    [actorRef],
  );

  const requestDeliveryPreferencesChange = useCallback(() => {
    actorRef.send({ type: 'REQUEST_DELIVERY_PREFERENCES_CHANGE' });
    trackDeliveryPreferencesPressed();
  }, [actorRef, trackDeliveryPreferencesPressed]);

  const changeDeliveryPreferences = useCallback(
    (params: ChangeDeliveryPreferences) => {
      actorRef.send({ type: 'CHANGE_DELIVERY_PREFERENCES', params });
    },
    [actorRef],
  );

  const changeDropoffLocation = useCallback(
    (dropoffLocation: string) => {
      const params = { dropoffLocation };
      actorRef.send({ type: 'CHANGE_DROPOFF_LOCATION', params });
      trackDropoffLocationChanged();
    },
    [actorRef, trackDropoffLocationChanged],
  );

  const addUpsell = useCallback(
    (params: AddUpsellToBag) => {
      actorRef.send({ type: 'ADD_UPSELL_TO_BAG', params });
    },
    [actorRef],
  );

  const startChangingLineItemQuantity = useCallback(
    (lineItemId: string) => {
      actorRef.send({ type: 'START_CHANGING_LINE_ITEM_QUANTITY', params: { lineItemId } });
    },
    [actorRef],
  );

  const stopChangingLineItemQuantity = useCallback(
    (lineItemId: string) => {
      actorRef.send({ type: 'STOP_CHANGING_LINE_ITEM_QUANTITY', params: { lineItemId } });
    },
    [actorRef],
  );

  const changeLineItemQuantity = useCallback(
    (params: ChangeLineItemQuantity) => {
      actorRef.send({ type: 'CHANGE_LINE_ITEM_QUANTITY', params });

      if (params.quantity > 0) {
        trackLineItemQuantityChanged();
        return;
      }

      trackLineItemRemoved();
    },
    [actorRef, trackLineItemQuantityChanged, trackLineItemRemoved],
  );

  const changeUtensilsUsage = useCallback(
    (params: ChangeUtensilsUsage) => {
      actorRef.send({ type: 'CHANGE_UTENSILS_USAGE', params });
      trackUtensilsUsagePressed();
    },
    [actorRef, trackUtensilsUsagePressed],
  );

  const showLoyaltyInfo = useCallback(() => {
    actorRef.send({ type: 'SHOW_LOYALTY_INFO' });
    trackLoyaltyInfoPressed();
  }, [actorRef, trackLoyaltyInfoPressed]);

  const showLoyaltyTerms = useCallback(
    (rewardId: string) => {
      actorRef.send({ type: 'SHOW_LOYALTY_TERMS', params: { rewardId } });
      trackLoyaltyTermsPressed();
    },
    [actorRef, trackLoyaltyTermsPressed],
  );

  const applyBagReward = useCallback(
    (rewardId: string) => {
      const params = { rewardId, orderId: context.cart.orderId ?? '' };
      actorRef.send({ type: 'APPLY_REWARD', params });
      trackApplyRewardPressed();
    },
    [context.cart.orderId, actorRef, trackApplyRewardPressed],
  );

  const removeBagReward = useCallback(
    (rewardId: string) => {
      const params = { rewardId, orderId: context.cart.orderId ?? '' };
      actorRef.send({ type: 'REMOVE_REWARD', params });
      trackRemoveRewardPressed();
    },
    [context.cart.orderId, actorRef, trackRemoveRewardPressed],
  );

  const refetchRewards = useCallback(() => {
    actorRef.send({ type: 'REFETCH_REWARDS' });
  }, [actorRef]);

  const applyBagPromoCode = useCallback(
    (code: string) => {
      actorRef.send({ type: 'APPLY_PROMO_CODE', params: { code } });
      trackPromoCodeAddPressed();
    },
    [actorRef, trackPromoCodeAddPressed],
  );

  const requestOrderPlayback = useCallback(
    (paymentMethodId?: string) => {
      const params = { paymentMethodId };
      actorRef.send({ type: 'SHOW_ORDER_PLAYBACK', params });
    },
    [actorRef],
  );

  const requestPaymentMethodChange = useCallback(() => {
    actorRef.send({ type: 'REQUEST_PAYMENT_METHOD_CHANGE' });
    trackChangePaymentMethodPressed();
  }, [actorRef, trackChangePaymentMethodPressed]);

  const changePaymentMethod = useCallback(
    (paymentMethodId: string) => {
      const params = { paymentMethodId };
      actorRef.send({ type: 'CHANGE_PAYMENT_METHOD', params });
    },
    [actorRef],
  );

  const finishPaymentSelection = useCallback(() => {
    actorRef.send({ type: 'FINISH_PAYMENT_SELECTION' });
  }, [actorRef]);

  const changeCreditUsage = useCallback(
    (shouldUseCredit: boolean) => {
      const params = { shouldUseCredit };
      actorRef.send({ type: 'CHANGE_CREDIT_USAGE', params });
      trackCreditPressed();
    },
    [actorRef, trackCreditPressed],
  );

  const changeFlexUsage = useCallback(
    (shouldUseFlex: boolean) => {
      const params = { shouldUseFlex };
      actorRef.send({ type: 'CHANGE_FLEX_USAGE', params });
      trackChangeFlexUsagePressed(shouldUseFlex);
    },
    [actorRef, trackChangeFlexUsagePressed],
  );

  const requestGiftCardForm = useCallback(() => {
    actorRef.send({ type: 'REQUEST_GIFT_CARD_FORM' });
    trackRedeemGiftCardPressed();
  }, [actorRef, trackRedeemGiftCardPressed]);

  const generateAwsWafTokenRef = useAwsWafTokenRef();

  const submitGiftCardForm = useCallback(
    async (code: string, regCode: string) => {
      const awsWafToken = await generateAwsWafTokenRef.current();
      const params = { code, regCode, awsWafToken };
      actorRef.send({ type: 'APPLY_GIFT_CARD', params });
      trackRedeemGiftCardSubmitted();
    },
    [actorRef, trackRedeemGiftCardSubmitted, generateAwsWafTokenRef],
  );

  // The stripe confirmation is triggered by the Stripe SDK.
  // It takes a few seconds so we need it to be in the machine to show loading states.
  const startStripeConfirmation = useCallback(() => {
    actorRef.send({ type: 'START_STRIPE_CONFIRMATION' });
    clearPaymentState();
  }, [actorRef, clearPaymentState]);

  const startOrderPolling = useCallback(
    (paymentMethodId?: string) => {
      const params = { paymentMethodId };
      actorRef.send({ type: 'CHANGE_PAYMENT_METHOD', params });
      actorRef.send({ type: 'HAS_STARTED_POLLING' });
      startPolling(context.orderId ?? context.cart?.orderId ?? '');
    },
    [actorRef, context.orderId, context.cart?.orderId, startPolling],
  );

  const requestOrderSubmission = useCallback(
    (paymentMethodId?: string) => {
      const params = { paymentMethodId };
      actorRef.send({ type: 'SUBMIT_ORDER', params });
    },
    [actorRef],
  );

  const requestGroupOrderConversion = useCallback(() => {
    actorRef.send({ type: 'REQUEST_GROUP_ORDER_CONVERSION' });
  }, [actorRef]);

  const convertToGroupOrder = useCallback(
    (params: ConvertToGroupOrderParams) => {
      actorRef.send({ type: 'CONVERT_TO_GROUP_ORDER', params });
    },
    [actorRef],
  );

  const requestGroupOrderShare = useCallback(() => {
    actorRef.send({ type: 'REQUEST_GROUP_ORDER_SHARE' });
  }, [actorRef]);

  const leaveGroupOrder = useCallback(() => {
    actorRef.send({ type: 'LEAVE_GROUP_ORDER' });
  }, [actorRef]);

  const requestGroupOrderLeave = useCallback(() => {
    actorRef.send({ type: 'REQUEST_GROUP_ORDER_LEAVE' });
  }, [actorRef]);

  const lockGroupOrder = useCallback(() => {
    actorRef.send({ type: 'CONFIRM_GROUP_ORDER_LOCK' });
  }, [actorRef]);

  const unlockGroupOrder = useCallback(() => {
    actorRef.send({ type: 'REQUEST_GROUP_ORDER_UNLOCK' });
  }, [actorRef]);

  const unlockCart = useCallback(() => {
    actorRef.send({ type: 'UNLOCK_CART' });
  }, [actorRef]);

  const assignRewards = useCallback(
    (rewards: BagReward[]) => {
      const data = { rewards };
      actorRef.send({ type: 'ASSIGN_REWARDS', data });
    },
    [actorRef],
  );

  const onRewardsFetchFailure = useCallback(() => {
    actorRef.send({ type: 'REWARDS_FETCH_ERROR' });
  }, [actorRef]);

  // ─── Total ─────────────────────────────────────────────────────────────────

  const { total, amountPaidWithStripe, creditToBeApplied } = useMemo(
    () =>
      prepareLedger({
        ledger: context.cart.ledger,
        tip: context.selectedTip,
        availableCredit: context.availableCredit,
        shouldUseCredit: context.shouldUseCredit,
        shouldUseFlex: context.shouldUseFlex,
        amountEligibleForFlex: context.amountEligibleForFlex,
      }),
    [
      context.cart.ledger,
      context.selectedTip,
      context.availableCredit,
      context.shouldUseCredit,
      context.shouldUseFlex,
      context.amountEligibleForFlex,
    ],
  );

  const requestCheckoutLedger = useCallback(() => {
    actorRef.send({ type: 'REQUEST_CHECKOUT_LEDGER' });
  }, [actorRef]);

  const closeBag = useCallback(() => {
    actorRef.send({ type: 'CLOSE_BAG' });
  }, [actorRef]);

  // ─── Upsell Items Filtering ────────────────────────────────────────────────────

  const upsellItems = useMemo(() => {
    return getMaxAvailableUpsellItems({
      lineItems: context.cart.lineItems,
      upsellItems: context.upsellItems,
      maxNumberOfUpsellProducts: context.maxNumberOfUpsellProducts,
    });
  }, [context.cart.lineItems, context.maxNumberOfUpsellProducts, context.upsellItems]);

  // ─── Deferred helpers ────────────────────────────────────────────────

  useBagLoyaltyRewardsDeferred({
    shouldFetchRewards: isFetchingRewardsDeferred,
    onSuccess: assignRewards,
    onFailure: onRewardsFetchFailure,
  });

  // ─── Back Behavior ───────────────────────────────────────────────────

  const canGoBack = isChangingPaymentMethod || isShowingGiftCardForm;

  const backBehavior = useCallback(() => {
    if (isChangingPaymentMethod) {
      actorRef.send({ type: 'REQUEST_CHECKOUT_LEDGER' });
    }

    if (isShowingGiftCardForm) {
      requestPaymentMethodChange();
    }
  }, [actorRef, isChangingPaymentMethod, isShowingGiftCardForm, requestPaymentMethodChange]);

  const goBack = canGoBack ? backBehavior : undefined;

  // ─── Return ────────────────────────────────────────────────────────────────

  return useMemo(
    () => ({
      ...context,

      upsellItems,

      bagTracking,
      total,
      amountPaidWithStripe,
      creditToBeApplied,
      registerStripeConfirmHandler,
      goBack,

      isFetchingInitialData,
      isFetchingRewards,
      hasFailedToLoadRewards,
      isChangingLineItemQuantity,
      isApplyingPromoCode,
      isChangingTime,
      isChangingDeliveryPreferences,
      isCancelingPayment,
      isSubmittingOrder,
      isPostOrder,
      isPollingPayment,
      isPaymentPending,
      isPaymentDelayed,
      isPaymentFulfilled,
      isApplyingGiftCard,
      isAddingUpsellToBag,
      isLineItemQuantityChangePending,
      isShowingGroupOrderConvertModal,
      isConvertingGroupOrder,
      isShowingGroupOrderShareModal,
      isShowingGroupOrderLeaveModal,
      isLeavingGroupOrder,
      isShowingGroupOrderLockModal,
      isLockingGroupOrder,
      isUnlockingGroupOrder,
      isPerformingBagAction,
      isShowingFutureDateWarning,
      isShowingRewardWarning,
      isShowingWarning,
      isShowingCheckoutLedger,
      isFetchingPaymentBreakdown,
      areUpsellInteractionsDisabled,
      isShowingOrderPlayback,
      isShowingTimeslotAdjustedNotice,
      isShowingNextDayOrderNotice,
      isChangingPaymentMethod,
      isShowingGiftCardForm,
      isShowingLoyaltyInfo,
      isShowingLoyaltyTerms,
      isShowingModal,
      isBagEmpty,
      isBagValid,

      startOrdering,
      startPostRedirectDelay,
      cancelRequest,
      cancelPreviousIntent: requestPaymentCancelation,
      createPayment,
      failPayment,
      setStripeFailedToLoad,
      flagFailureAsShown,
      setConfirmStripe,
      requestChannelOrLocationChange,
      requestTimeChange,
      changeTime,
      startWantedTimesPolling,
      stopWantedTimesPolling,
      startBagCartPolling,
      stopBagCartPolling,
      changeTip,
      requestDeliveryPreferencesChange,
      changeDeliveryPreferences,
      changeDropoffLocation,
      addUpsell,
      startChangingLineItemQuantity,
      stopChangingLineItemQuantity,
      changeLineItemQuantity,
      changeUtensilsUsage,
      showLoyaltyInfo,
      showLoyaltyTerms,
      applyBagReward,
      removeBagReward,
      refetchRewards,
      applyBagPromoCode,
      requestCheckoutLedger,
      closeBag,
      requestOrderPlayback,
      requestPaymentMethodChange,
      changePaymentMethod,
      finishPaymentSelection,
      changeCreditUsage,
      changeFlexUsage,
      requestGiftCardForm,
      generateAwsWafTokenRef,
      submitGiftCardForm,
      startStripeConfirmation,
      startOrderPolling,
      requestOrderSubmission,
      requestGroupOrderConversion,
      convertToGroupOrder,
      requestGroupOrderShare,
      requestGroupOrderLeave,
      leaveGroupOrder,
      lockGroupOrder,
      unlockGroupOrder,
      unlockCart,
    }),
    [
      addUpsell,
      applyBagPromoCode,
      applyBagReward,
      bagTracking,
      requestPaymentCancelation,
      createPayment,
      failPayment,
      cancelRequest,
      changeCreditUsage,
      changeFlexUsage,
      changeDeliveryPreferences,
      changeDropoffLocation,
      changeLineItemQuantity,
      changePaymentMethod,
      changeTime,
      changeTip,
      changeUtensilsUsage,
      context,
      creditToBeApplied,
      finishPaymentSelection,
      setStripeFailedToLoad,
      flagFailureAsShown,
      goBack,
      hasFailedToLoadRewards,
      isAddingUpsellToBag,
      isApplyingGiftCard,
      isApplyingPromoCode,
      isBagEmpty,
      isBagValid,
      isCancelingPayment,
      isChangingDeliveryPreferences,
      isChangingLineItemQuantity,
      isChangingPaymentMethod,
      isChangingTime,
      isFetchingInitialData,
      isFetchingRewards,
      isLineItemQuantityChangePending,
      isShowingGroupOrderConvertModal,
      isConvertingGroupOrder,
      isShowingGroupOrderShareModal,
      isShowingGroupOrderLeaveModal,
      isLeavingGroupOrder,
      isShowingGroupOrderLockModal,
      isLockingGroupOrder,
      isUnlockingGroupOrder,
      isPerformingBagAction,
      isPostOrder,
      isPollingPayment,
      isPaymentPending,
      isPaymentDelayed,
      isPaymentFulfilled,
      isShowingCheckoutLedger,
      isFetchingPaymentBreakdown,
      areUpsellInteractionsDisabled,
      isShowingFutureDateWarning,
      isShowingGiftCardForm,
      isShowingLoyaltyInfo,
      isShowingLoyaltyTerms,
      isShowingModal,
      isShowingNextDayOrderNotice,
      isShowingOrderPlayback,
      isShowingRewardWarning,
      isShowingTimeslotAdjustedNotice,
      isShowingWarning,
      isSubmittingOrder,
      upsellItems,
      refetchRewards,
      startWantedTimesPolling,
      stopWantedTimesPolling,
      startBagCartPolling,
      stopBagCartPolling,
      registerStripeConfirmHandler,
      removeBagReward,
      requestChannelOrLocationChange,
      requestCheckoutLedger,
      closeBag,
      requestDeliveryPreferencesChange,
      requestGiftCardForm,
      generateAwsWafTokenRef,
      requestOrderPlayback,
      requestOrderSubmission,
      requestGroupOrderConversion,
      convertToGroupOrder,
      requestGroupOrderShare,
      requestGroupOrderLeave,
      leaveGroupOrder,
      lockGroupOrder,
      unlockGroupOrder,
      unlockCart,
      requestPaymentMethodChange,
      requestTimeChange,
      setConfirmStripe,
      showLoyaltyInfo,
      showLoyaltyTerms,
      startChangingLineItemQuantity,
      startOrderPolling,
      startOrdering,
      startPostRedirectDelay,
      startStripeConfirmation,
      stopChangingLineItemQuantity,
      submitGiftCardForm,
      total,
      amountPaidWithStripe,
    ],
  );
};

// ─── Types ───────────────────────────────────────────────────────────────────

type BagTracking = {
  trackBagView: () => void;
  trackInitiatedCheckout: (payload: {
    price: number;
    productIds: string;
    orderType: string;
    quantity: number;
  }) => void;
  trackLocationNamePressed: () => void;
  trackWantedTimePressed: () => void;
  trackWantedTimeChanged: () => void;
  trackDeliveryPreferencesPressed: () => void;
  trackDropoffLocationChanged: () => void;
  trackViewMenuPressed: () => void;
  trackLineItemPressed: () => void;
  trackLineItemRemoved: () => void;
  trackLineItemQuantityChanged: () => void;
  trackLoyaltyInfoPressed: () => void;
  trackLoyaltyTermsPressed: () => void;
  trackLoyaltySignedOutPressed: () => void;
  trackApplyRewardPressed: () => void;
  trackRemoveRewardPressed: () => void;
  trackPromoCodeAddPressed: () => void;
  trackUtensilsUsagePressed: () => void;
  trackContinuePressed: () => void;
  trackContinueDeliveryMinimumBlocked: (
    minimum: string | number,
    amountNeeded: string | number,
  ) => void;
  trackChangePaymentMethodPressed: () => void;
  trackCreditPressed: () => void;
  trackChangeFlexUsagePressed: (toggled: boolean) => void;
  trackPaymentMethodPressed: () => void;
  trackRedeemGiftCardPressed: () => void;
  trackRedeemGiftCardSubmitted: () => void;
  trackOrderPlaybackAutoSubmit: () => void;
  trackOrderPlaybackGoBack: () => void;
};

type UseOrderingParams = {
  isLoggedIn: boolean;
  orderSubmissionDelay: number;
  bagCartPollingInterval: number;
  bagTracking: BagTracking;
  handlePostOrderPurchaseModal: OrderingMachineAction;
  handlePostOrderNotifications: OrderingMachineAction;
  handlePostOrderTracking: OrderingMachineAction;
  clearSharedLineItemAttribution: OrderingMachineAction;
  handlePostOrderHistory: OrderingMachineAction;
  handlePostOrderAppReview: OrderingMachineAction;
  navigatePostOrder: OrderingMachineAction;
  changeChannelOrLocation: OrderingMachineAction;
  requestLogin: OrderingMachineAction;
  handleConvertToGroupOrderError: OrderingMachineAction<ConvertToGroupOrderErrorParams>;
  handleLeaveGroupOrderSuccess: OrderingMachineAction;
  handleLeaveGroupOrderError: OrderingMachineAction<LeaveGroupOrderErrorParams>;
  handleLockGroupOrderError: OrderingMachineAction<LockGroupOrderErrorParams>;
  unlockCartInBackground: OrderingMachineAction;
  closeBag: OrderingMachineAction;
  handleUnlockGroupOrderError: OrderingMachineAction;

  fetchInitialData: typeof OrderingActors.fetchInitialData;
  fetchStatePostOrder: typeof OrderingActors.fetchStatePostOrder;
  fetchRewards: typeof OrderingActors.fetchRewards;
  fetchWantedTimes: typeof OrderingActors.fetchWantedTimes;
  fetchBagCartPolling: typeof OrderingActors.fetchBagCartPolling;
  addUpsellToBag: typeof OrderingActors.addUpsellToBag;
  updateLineItem: typeof OrderingActors.updateLineItem;
  applyReward: typeof OrderingActors.applyReward;
  removeReward: typeof OrderingActors.removeReward;
  applyPromoCode: typeof OrderingActors.applyPromoCode;
  applyGiftCard: typeof OrderingActors.applyGiftCard;
  submitOrder: typeof OrderingActors.submitOrder;
  convertToGroupOrder: typeof OrderingActors.convertToGroupOrder;
  leaveGroupOrder: typeof OrderingActors.leaveGroupOrder;
  lockGroupOrder: typeof OrderingActors.lockGroupOrder;
  unlockGroupOrder: typeof OrderingActors.unlockGroupOrder;

  fetchOrderPaymentState: typeof PaymentStatePollingActors.fetchPaymentState;
  cancelPreviousIntent: typeof PaymentStatePollingActors.cancelPayment;
};
